import { AdListingImagePicker } from "@/components/catalog/AdListingImagePicker";
import { SalePriceInput } from "@/components/catalog/SalePriceField";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatSalePriceNumber } from "@/lib/formatPkrAmount";
import type { UploadedFile } from "@/lib/catalog/types";
import { cn } from "@/lib/utils";
import { format, parseISO } from "date-fns";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";

export type ProductPostingPlatform = "pakwheels" | "olx" | "wisewheels";

/** Saved marketplace row when reopening the dialog. */
export type ProductPostingExistingListing = {
  id: number;
  salePrice: number | null;
  images: UploadedFile[];
  platformLink: string | null;
  createdAt: string;
};

type ProductPostingAdDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  platform: ProductPostingPlatform;
  headline: string;
  productImages: UploadedFile[];
  price: string;
  location: string;
  description: string;
  /** When set, sale price must be >= this value (PKR). */
  minSalePricePkr?: number | null;
  /** When set, listing already exists and can be updated. */
  existingListing?: ProductPostingExistingListing | null;
  onConfirm: (payload: { salePrice: number | null; images: UploadedFile[] }) => void | Promise<void>;
};

const PLATFORM_UI: Record<
  ProductPostingPlatform,
  {
    title: string;
    brandLabel: string;
    description: ReactNode;
    confirmClassName: string;
    footerNote: ReactNode;
    mockupKind: "pakwheels" | "olx" | "wisewheels";
  }
> = {
  pakwheels: {
    title: "Post to PakWheels",
    brandLabel: "PakWheels",
    description: (
      <>
        Choose which product photos to include on <strong className="text-foreground">PakWheels</strong>, set the sale price, then save.
      </>
    ),
    confirmClassName: "bg-orange-600 text-white hover:bg-orange-700",
    footerNote: (
      <>
        Saving stores the selected images and sale price for this product on <strong className="text-foreground">PakWheels</strong>.
      </>
    ),
    mockupKind: "pakwheels",
  },
  olx: {
    title: "Post to OLX",
    brandLabel: "OLX",
    description: (
      <>
        Choose which product photos to include on <strong className="text-foreground">OLX</strong>, set the sale price, then save.
      </>
    ),
    confirmClassName: "bg-emerald-600 text-white hover:bg-emerald-700",
    footerNote: (
      <>
        Saving stores the selected images and sale price for this product on <strong className="text-foreground">OLX</strong>.
      </>
    ),
    mockupKind: "olx",
  },
  wisewheels: {
    title: "Post to Wise Wheels",
    brandLabel: "Wise Wheels",
    description: (
      <>
        Choose which product photos to include on <strong className="text-foreground">Wise Wheels</strong>, set the sale price, then save.
      </>
    ),
    confirmClassName: "bg-sky-600 text-white hover:bg-sky-700",
    footerNote: (
      <>
        Saving stores the selected images and sale price for this product on <strong className="text-foreground">Wise Wheels</strong>.
      </>
    ),
    mockupKind: "wisewheels",
  },
};

function ClassifiedAdMockup({
  kind,
  headline,
  imageUrl,
  price,
  location,
  description,
}: {
  kind: "pakwheels" | "olx" | "wisewheels";
  headline: string;
  imageUrl?: string | null;
  price: string;
  location: string;
  description: string;
}) {
  const accent =
    kind === "pakwheels"
      ? "border-orange-500 bg-orange-500/10 text-orange-800 dark:text-orange-200"
      : kind === "olx"
        ? "border-emerald-600 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200"
        : "border-sky-600 bg-sky-500/10 text-sky-900 dark:text-sky-100";
  const label = kind === "pakwheels" ? "PakWheels" : kind === "olx" ? "OLX" : "Wise Wheels";
  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border-2 border-border bg-muted/30 shadow-sm",
        kind === "pakwheels"
          ? "ring-1 ring-orange-500/20"
          : kind === "olx"
            ? "ring-1 ring-emerald-600/20"
            : "ring-1 ring-sky-500/25",
      )}
    >
      <div className={cn("border-b-2 px-3 py-2 text-xs font-bold tracking-wide", accent)}>{label} style preview</div>
      <div className="flex flex-col sm:flex-row sm:min-h-[200px]">
        <div className="min-h-[140px] w-full border-b border-border bg-muted/50 sm:w-2/5 sm:min-h-[200px] sm:border-b-0 sm:border-r">
          {imageUrl ? (
            <img src={imageUrl} alt="" className="h-full min-h-[140px] w-full object-cover sm:min-h-[200px]" />
          ) : (
            <div className="flex h-full min-h-[140px] items-center justify-center p-4 text-center text-sm text-muted-foreground sm:min-h-[200px]">
              Select at least one photo
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-2 p-4">
          <h4 className="line-clamp-2 text-lg font-bold leading-tight text-foreground">{headline}</h4>
          <p className="text-xl font-bold tabular-nums text-primary">{price}</p>
          {location ? (
            <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-muted-foreground/50" />
              {location}
            </p>
          ) : null}
          <p className="mt-1 line-clamp-4 border-t border-border/60 pt-2 text-sm leading-relaxed text-muted-foreground">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
}

function initialSelectedIds(productImages: UploadedFile[], existingListing?: ProductPostingExistingListing | null): Set<string> {
  const visible = productImages.filter((img) => Boolean(img.url));
  if (existingListing?.images?.length) {
    const saved = new Set(existingListing.images.map((img) => img.id));
    const matched = visible.filter((img) => saved.has(img.id)).map((img) => img.id);
    if (matched.length > 0) return new Set(matched);
  }
  return new Set(visible.map((img) => img.id));
}

/**
 * Marketplace ad dialog: pick listing images, set sale price, save to product_ad_listings.
 */
export function ProductPostingAdDialog({
  open,
  onOpenChange,
  platform,
  headline,
  productImages,
  price,
  location,
  description,
  minSalePricePkr,
  existingListing,
  onConfirm,
}: ProductPostingAdDialogProps) {
  const [salePrice, setSalePrice] = useState<number | null>(null);
  const [saleError, setSaleError] = useState<string | null>(null);
  const [imagesError, setImagesError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [saving, setSaving] = useState(false);
  const ui = PLATFORM_UI[platform];
  const isPosted = Boolean(existingListing);

  const visibleImages = useMemo(() => productImages.filter((img) => Boolean(img.url)), [productImages]);

  useEffect(() => {
    if (!open) return;
    setSaving(false);
    setSaleError(null);
    setImagesError(null);
    setSelectedIds(initialSelectedIds(productImages, existingListing));
    if (existingListing) {
      const sp = existingListing.salePrice;
      if (sp != null && Number.isFinite(Number(sp)) && Number(sp) > 0) {
        setSalePrice(Math.floor(Number(sp)));
      } else {
        setSalePrice(null);
      }
    } else {
      setSalePrice(null);
    }
  }, [open, platform, headline, existingListing?.id, productImages]);

  const selectedImages = useMemo(
    () => visibleImages.filter((img) => selectedIds.has(img.id)),
    [visibleImages, selectedIds],
  );

  const previewPriceLine = useMemo(() => {
    if (salePrice != null && salePrice > 0) {
      return `₨ ${formatSalePriceNumber(salePrice)}`;
    }
    return price;
  }, [price, salePrice]);

  const previewImageUrl = selectedImages[0]?.url ?? null;
  const idBase = `ad-${platform}`;

  const postedOnLabel = useMemo(() => {
    if (!existingListing?.createdAt) return null;
    try {
      return format(parseISO(existingListing.createdAt), "PPp");
    } catch {
      return existingListing.createdAt;
    }
  }, [existingListing?.createdAt]);

  function toggleImage(id: string) {
    setImagesError(null);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAllImages() {
    setImagesError(null);
    setSelectedIds(new Set(visibleImages.map((img) => img.id)));
  }

  function clearAllImages() {
    setImagesError(null);
    setSelectedIds(new Set());
  }

  async function handleSave() {
    const min = minSalePricePkr != null && Number.isFinite(Number(minSalePricePkr)) ? Number(minSalePricePkr) : null;
    if (min != null && salePrice != null && salePrice < min) {
      setSaleError(`Sale price cannot be less than base price (₨ ${formatSalePriceNumber(min)}).`);
      queueMicrotask(() => document.getElementById(`${idBase}-sale-total`)?.focus());
      return;
    }
    if (visibleImages.length > 0 && selectedImages.length === 0) {
      setImagesError("Select at least one image to post.");
      return;
    }
    setSaving(true);
    try {
      await onConfirm({ salePrice, images: selectedImages });
      onOpenChange(false);
    } catch {
      /* parent shows error toast */
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (saving && !next) return;
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-[min(100vw-1.5rem,48rem)] max-h-[92vh] gap-0 overflow-y-auto p-0 sm:max-w-3xl">
        <DialogHeader className="px-6 pb-2 pl-6 pr-12 pt-6 text-left">
          <DialogTitle>{isPosted ? `Update ${ui.brandLabel} listing` : ui.title}</DialogTitle>
          <DialogDescription>{ui.description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 px-6">
          {isPosted && existingListing ? (
            <div className="rounded-xl border border-border bg-muted/25 px-3 py-3 text-sm sm:px-4">
              <div className="flex flex-wrap items-center gap-2 gap-y-1.5">
                <span className="inline-flex items-center rounded-md border border-emerald-600/40 bg-emerald-500/15 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-emerald-800 dark:text-emerald-200">
                  Posted
                </span>
                <span className="text-muted-foreground">on {ui.brandLabel}</span>
              </div>
              {postedOnLabel ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground/80">Saved at</span> {postedOnLabel}
                </p>
              ) : null}
              {existingListing.platformLink ? (
                <p className="mt-1.5 min-w-0 text-xs">
                  <span className="font-medium text-foreground/80">Listing link</span>{" "}
                  <a
                    href={existingListing.platformLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="break-all text-primary underline-offset-2 hover:underline"
                  >
                    {existingListing.platformLink}
                  </a>
                </p>
              ) : (
                <p className="mt-1.5 text-xs text-muted-foreground">No external listing URL stored.</p>
              )}
            </div>
          ) : null}

          <div
            className="space-y-3 rounded-xl border border-border bg-muted/20 p-3 shadow-sm sm:p-4"
            role="group"
            aria-labelledby={`${idBase}-images-heading`}
          >
            <div id={`${idBase}-images-heading`} className="sr-only">
              Listing images
            </div>
            <AdListingImagePicker
              images={productImages}
              selectedIds={selectedIds}
              onToggle={toggleImage}
              onSelectAll={selectAllImages}
              onClearAll={clearAllImages}
              disabled={saving}
            />
            {imagesError ? <p className="text-xs text-destructive">{imagesError}</p> : null}
          </div>

          <div
            className="space-y-3 rounded-xl border border-border bg-muted/20 p-3 shadow-sm sm:p-4"
            role="group"
            aria-labelledby={`${idBase}-sale-heading`}
          >
            <div id={`${idBase}-sale-heading`} className="text-sm font-semibold leading-tight text-foreground">
              Sale price (PKR)
            </div>
            <SalePriceInput
              id={`${idBase}-sale-total`}
              label="Total PKR (saved)"
              value={salePrice}
              onChange={(next) => {
                if (saleError) setSaleError(null);
                setSalePrice(next);
              }}
              error={saleError}
              disabled={saving}
              inputClassName="h-9 font-medium sm:h-10"
            />
          </div>

          <ClassifiedAdMockup
            kind={ui.mockupKind}
            headline={headline}
            imageUrl={previewImageUrl}
            price={previewPriceLine}
            location={location}
            description={description}
          />

          <p className="text-sm text-muted-foreground">{ui.footerNote}</p>
        </div>
        <DialogFooter className="gap-2 border-t border-border px-6 py-4 sm:justify-end">
          <Button type="button" variant="ghost" disabled={saving} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" className={cn("gap-2", ui.confirmClassName)} disabled={saving} onClick={() => void handleSave()}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
